import React, { createContext, useContext, useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { registrarEdicaoTrilha } from "../../../shared/utils/trilhaAuditoria";
import { useAuth } from "../../auth/context/AuthContext";

export interface Pendencia {
  id: string;
  pacienteId: string;
  pacienteNome: string;
  descricao: string;
  criadoEm: string;
  criadoPor: string;
  criadoPorNome: string;
  evolucaoId?: string;
  concluido: boolean;
  concluidoEm?: string;
  concluidoPorNome?: string;
}

interface PendenciasContextType {
  pendencias: Pendencia[];
  adicionarPendencias: (
    pacienteId: string,
    pacienteNome: string,
    descricoes: string[],
    evolucaoId?: string
  ) => Promise<void>;
  concluirPendencia: (id: string) => Promise<void>;
  carregarPendencias: () => Promise<void>;
}

const PendenciasContext = createContext<PendenciasContextType | null>(null);

export function PendenciasProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { usuarioLogado } = useAuth();
  const [pendencias, setPendencias] = useState<Pendencia[]>([]);

  const carregarPendencias = async () => {
    try {
      const q = query(
        collection(db, "pendencias"),
        where("concluido", "==", false),
        orderBy("criadoEm", "desc")
      );
      const snapshot = await getDocs(q);
      const lista: Pendencia[] = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          pacienteId: data.pacienteId,
          pacienteNome: data.pacienteNome,
          descricao: data.descricao,
          criadoEm:
            data.criadoEm instanceof Timestamp
              ? data.criadoEm.toDate().toISOString()
              : data.criadoEm || "",
          criadoPor: data.criadoPor,
          criadoPorNome: data.criadoPorNome,
          evolucaoId: data.evolucaoId,
          concluido: data.concluido,
          concluidoEm: data.concluidoEm,
          concluidoPorNome: data.concluidoPorNome,
        };
      });
      setPendencias(lista);
    } catch (error) {
      console.error("Erro ao carregar pendências:", error);
    }
  };

  const adicionarPendencias = async (
    pacienteId: string,
    pacienteNome: string,
    descricoes: string[],
    evolucaoId?: string
  ) => {
    if (!usuarioLogado) return;
    try {
      for (const descricao of descricoes) {
        if (!descricao.trim()) continue;
        await addDoc(collection(db, "pendencias"), {
          pacienteId,
          pacienteNome,
          descricao: descricao.trim(),
          criadoEm: serverTimestamp(),
          criadoPor: usuarioLogado.id,
          criadoPorNome: usuarioLogado.nome,
          evolucaoId: evolucaoId || null,
          concluido: false,
          concluidoEm: null,
          concluidoPorNome: null,
        });
      }
      await carregarPendencias();
    } catch (error) {
      console.error("Erro ao adicionar pendências:", error);
      throw error;
    }
  };

  const concluirPendencia = async (id: string) => {
    if (!usuarioLogado) return;
    try {
      await updateDoc(doc(db, "pendencias", id), {
        concluido: true,
        concluidoEm: new Date().toISOString(),
        concluidoPorNome: usuarioLogado.nome,
      });

      // B213 — conclusão é a única edição possível de pendência
      const atual = pendencias.find((p) => p.id === id);
      await registrarEdicaoTrilha({
        colecao: "pendencias",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: "Pendência concluída",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        camposAlterados: ["concluido"],
      });

      await carregarPendencias();
    } catch (error) {
      console.error("Erro ao concluir pendência:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (usuarioLogado) carregarPendencias();
  }, [usuarioLogado]);

  return (
    <PendenciasContext.Provider
      value={{
        pendencias,
        adicionarPendencias,
        concluirPendencia,
        carregarPendencias,
      }}
    >
      {children}
    </PendenciasContext.Provider>
  );
}

export function usePendencias() {
  const ctx = useContext(PendenciasContext);
  if (!ctx)
    throw new Error(
      "usePendencias deve ser usado dentro de PendenciasProvider"
    );
  return ctx;
}
